/**
 * LOCALIZAÇÃO E FOTO — as duas coisas que o navegador só dá com permissão.
 *
 * Tudo aqui é otimista com a rede ruim da obra e pessimista com o navegador:
 * cada função tem prazo, mensagem em português e caminho de volta. Uma tela de
 * ponto que trava esperando GPS é uma tela em que ninguém consegue bater.
 *
 * DUAS ARMADILHAS QUE CUSTARAM CARO EM OUTROS SISTEMAS DA CASA:
 *
 * 1. GEOLOCALIZAÇÃO SÓ FUNCIONA EM HTTPS (ou em localhost). Fora disso o
 *    navegador NEGA sem perguntar nada ao usuário, e a mensagem padrão não diz
 *    isso — é o mesmo tropeço do carimbo de GPS nas fotos do Relatórios.
 *    Por isso a mensagem de erro aqui nomeia a causa quando a página não é
 *    segura, em vez de mandar "autorize a localização" para quem não tem o que
 *    autorizar.
 *
 * 2. A FOTO VEM DA CÂMERA, NUNCA DE ARQUIVO. Foto escolhida da galeria do
 *    iPhone chega em HEIC, que o Chrome não abre — é a mesma dor das fotos dos
 *    colaboradores no RH. Capturando do `<video>` para um `<canvas>`, o que sai
 *    é sempre JPEG, em qualquer aparelho. De quebra, impede mandar a foto de
 *    ontem como se fosse de agora.
 */

export interface Local {
  latitude: number;
  longitude: number;
  precisao_m: number;
}

/** A página está num contexto em que o navegador libera câmera e GPS? */
export function contextoSeguro(): boolean {
  return window.isSecureContext;
}

/**
 * Pede a localização ao navegador.
 *
 * `enableHighAccuracy` liga o GPS de verdade em vez de estimar pela rede: é mais
 * lento e gasta bateria, mas uma localização de 3 km de raio não prova nada
 * sobre onde a pessoa estava.
 */
export function obterLocal(prazoMs = 15000): Promise<Local> {
  return new Promise((resolve, reject) => {
    if (!("geolocation" in navigator)) {
      reject(new Error("Este aparelho não informa a localização."));
      return;
    }
    if (!contextoSeguro()) {
      reject(
        new Error(
          "A localização só funciona em endereço seguro (https). Abra o ponto pelo endereço oficial da empresa.",
        ),
      );
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          precisao_m: pos.coords.accuracy,
        }),
      (err) => {
        // Cada código tem uma saída diferente para o usuário, e mandar "erro de
        // localização" para os três deixa a pessoa sem saber o que fazer.
        if (err.code === err.PERMISSION_DENIED) {
          reject(
            new Error(
              "A localização está bloqueada. Toque no cadeado ao lado do endereço e permita a localização.",
            ),
          );
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          reject(new Error("Não foi possível obter o sinal de GPS. Vá para um local aberto e tente de novo."));
        } else {
          reject(new Error("O GPS demorou para responder. Tente novamente."));
        }
      },
      { enableHighAccuracy: true, timeout: prazoMs, maximumAge: 0 },
    );
  });
}

/**
 * Liga a câmera frontal.
 *
 * `facingMode: "user"` pede a frontal porque a foto é uma selfie de conferência.
 * `ideal` e não `exact`: em aparelho que só tem uma câmera, `exact` falharia e
 * a pessoa ficaria sem bater — melhor a câmera de trás que câmera nenhuma.
 */
export async function ligarCamera(): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error("Este navegador não abre a câmera. Use o Chrome ou o Safari atualizado.");
  }
  if (!contextoSeguro()) {
    throw new Error(
      "A câmera só funciona em endereço seguro (https). Abra o ponto pelo endereço oficial da empresa.",
    );
  }

  try {
    return await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { ideal: "user" }, width: { ideal: 640 }, height: { ideal: 640 } },
      audio: false,
    });
  } catch (err) {
    const nome = (err as Error)?.name ?? "";
    if (nome === "NotAllowedError") {
      throw new Error(
        "A câmera está bloqueada. Toque no cadeado ao lado do endereço e permita a câmera.",
      );
    }
    if (nome === "NotFoundError") {
      throw new Error("Nenhuma câmera encontrada neste aparelho.");
    }
    if (nome === "NotReadableError") {
      throw new Error("A câmera está em uso por outro aplicativo. Feche-o e tente de novo.");
    }
    throw new Error("Não foi possível abrir a câmera.");
  }
}

/** Desliga a câmera. Sem isto, a luz do celular fica acesa depois da batida. */
export function desligarCamera(stream: MediaStream | null) {
  stream?.getTracks().forEach((t) => t.stop());
}

/**
 * Captura um quadro do vídeo como JPEG (data URI).
 *
 * `lado` limita a imagem a um quadrado pequeno: a foto serve para reconhecer a
 * pessoa, não para ampliar. Uma selfie de 12 megapixels no 4G da obra é a
 * diferença entre bater o ponto e desistir — e o banco guarda uma por batida,
 * quatro por dia, por pessoa.
 */
export function capturarFoto(video: HTMLVideoElement, lado = 480, qualidade = 0.7): string {
  const largura = video.videoWidth || lado;
  const altura = video.videoHeight || lado;
  // Recorta o quadrado central: o vídeo costuma ser 4:3 e o rosto fica no meio.
  const corte = Math.min(largura, altura);
  const x = (largura - corte) / 2;
  const y = (altura - corte) / 2;

  const canvas = document.createElement("canvas");
  canvas.width = lado;
  canvas.height = lado;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Não foi possível preparar a foto.");

  // A câmera frontal mostra a imagem espelhada na tela; sem o espelhamento aqui,
  // a foto salva sai invertida em relação ao que a pessoa viu ao tirar.
  ctx.translate(lado, 0);
  ctx.scale(-1, 1);
  ctx.drawImage(video, x, y, corte, corte, 0, 0, lado, lado);

  return canvas.toDataURL("image/jpeg", qualidade);
}
